import { Button } from '@mui/material'
import { TraceKey } from '@ulysses-inc/harami_api_client'
import { FC, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import { Header } from 'src/components/header/Header'
import { HeaderBackButtonMui } from 'src/components/header/HeaderBackButtonMui'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import Loading from 'src/components/loading/Loading'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { getContractPlan } from 'src/features/auth/getContractPlan'
import { useMutationUpdateTraceKey } from 'src/features/traceKeys/api/useMutationUpdateTraceKey'
import { useQueryTraceKeys } from 'src/features/traceKeys/api/useQueryTraceKeys'
import { isTraceKeySupportedPlan } from 'src/features/traceKeys/utils/traceKeyLimit'
import { isHTTPError } from 'src/state/middleware/saga/handleHttpError'
import {
  EditTraceKeyForm,
  EditTraceKeyFormData,
} from './components/EditTraceKeyForm'

type RouteParams = {
  traceKeyId: string
}

/**
 * 検索キー編集ページ
 * データ取得とルーティングを担当
 */
const EditTraceKeyPage: FC = () => {
  const history = useHistory()
  const { traceKeyId } = useParams<RouteParams>()
  const traceKeyIdNum = Number(traceKeyId)

  const { data: traceKeys = [], isLoading } = useQueryTraceKeys()
  const traceKey = traceKeys.find(tk => tk.id === traceKeyIdNum)

  if (isLoading) {
    return <Loading />
  }

  const plan = getContractPlan()
  // このページ自体 TraceKeySupportedPlan 以外のプランでは表示されないはずだが念のため制御している
  if (!isTraceKeySupportedPlan(plan)) {
    throw new Error(`このページは${plan}プランでは利用できません`)
  }

  if (!traceKey) {
    history.push('/traceKeys')
    return null
  }

  return <EditTraceKeyContent traceKey={traceKey} />
}

type EditTraceKeyContentProps = {
  traceKey: TraceKey
}

/**
 * 検索キー編集フォームのコンテンツ
 * traceKeyが確定した状態でフォームを初期化
 */
const EditTraceKeyContent: FC<EditTraceKeyContentProps> = ({ traceKey }) => {
  const history = useHistory()
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar()
  const updateTraceKeyMutation = useMutationUpdateTraceKey()

  const form = useForm<EditTraceKeyFormData>({
    defaultValues: {
      name: traceKey.name,
      responseType: traceKey.responseType,
    },
  })

  const handleBack = useCallback(() => {
    history.push('/traceKeys')
  }, [history])

  const onSubmit = useCallback(
    (data: EditTraceKeyFormData) => {
      updateTraceKeyMutation.mutate(
        {
          traceKeyId: traceKey.id,
          name: data.name,
        },
        {
          onSuccess: () => {
            showSuccessSnackbar(`「${data.name}」の更新に成功しました`)
            history.push('/traceKeys')
          },
          onError: (error: unknown) => {
            if (isHTTPError(error)) {
              switch (error.status) {
                case 400:
                case 404:
                case 409:
                  showErrorSnackbar(error.errors.join('\n'))
                  break
                default:
                  showErrorSnackbar(`「${data.name}」の更新に失敗しました`)
                  break
              }
              return
            }
            showErrorSnackbar(`「${data.name}」の更新に失敗しました`)
          },
        },
      )
    },
    [
      updateTraceKeyMutation,
      showSuccessSnackbar,
      showErrorSnackbar,
      history,
      traceKey.id,
    ],
  )

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Header
        sticky
        rightSlot={
          <Button
            type="submit"
            variant="contained"
            disabled={updateTraceKeyMutation.isPending}
          >
            {updateTraceKeyMutation.isPending ? '保存中...' : '保存'}
          </Button>
        }
      >
        <HeaderBackButtonMui
          onClick={handleBack}
          disabled={updateTraceKeyMutation.isPending}
        />
        <HeaderTitle title="検索キーの編集" />
      </Header>

      <EditTraceKeyForm form={form} responseType={traceKey.responseType} />
    </form>
  )
}

export { EditTraceKeyPage }
